import { Accordion } from "@via/components/accordion/accordion";
import {
  CheckImportViaPipelineEntitiesCheckResult,
  ImportConflictResolution,
  ImportRevisionConflict,
  ImportRevisionReference,
} from "common";

/**
 * Plain wording for the places a revision can be depended on. The backend reports the
 * table it counted rows in, which is precise but means nothing to a scientist reading
 * "run_process_provenance". Anything not listed here falls back to the raw name rather
 * than being hidden, so a source added later still shows up.
 */
const REFERENCE_LABELS: Record<string, string> = {
  run_process_provenance: "pipeline runs that already used it",
  biocorepipe_save: "pipelines that include it",
  standalone_process_run: "standalone runs of it",
  process_revision_publication: "a published copy in a Git repository",
  ai_model: "AI models built on it",
  model_skill: "model skills built on it",
};

const describeReference = (reference: ImportRevisionReference): string =>
  `${REFERENCE_LABELS[reference.source] ?? reference.source} (${reference.count})`;

const describeReferences = (references: ImportRevisionReference[]): string =>
  references.map(describeReference).join(", ");

/**
 * What each choice does, said the way it will actually happen. These are the only three
 * choices there are. There is no fork: a forked revision would carry an identity no other
 * installation has ever seen, and could never be shared back.
 */
const RESOLUTION_COPY: Record<
  ImportConflictResolution,
  { label: string; description: string }
> = {
  [ImportConflictResolution.KeepLocal]: {
    label: "Keep the version already here",
    description:
      "The imported pipeline will use the version this installation already holds. Nothing is written, nothing is deleted, and the version in the file is discarded. This is the safe choice.",
  },
  [ImportConflictResolution.Replace]: {
    label: "Delete the version here and use the incoming one",
    description:
      "The version stored here is permanently deleted and the version from the file takes its place. This cannot be undone, so pick it only when you know the file carries the version you want.",
  },
  [ImportConflictResolution.Restore]: {
    label: "Bring back the removed version already here",
    description:
      "This installation already holds this exact version, but somebody removed it. Bringing it back gives you the same content the file carries. Nothing is overwritten.",
  },
};

/**
 * The wording for one choice, with the one caveat that depends on the situation: keeping
 * a version that was removed leaves it removed, and a user should not have to work that
 * out from the fact list above. This reads `localDeleted` to phrase the choice, never to
 * decide whether the choice is on offer.
 */
const resolutionCopy = (
  resolution: ImportConflictResolution,
  localDeleted: boolean
): { label: string; description: string } => {
  const copy = RESOLUTION_COPY[resolution];
  if (!copy) {
    return { label: resolution, description: "" };
  }
  if (resolution === ImportConflictResolution.KeepLocal && localDeleted) {
    return {
      ...copy,
      description: `${copy.description} The version here was removed, and this choice leaves it removed.`,
    };
  }
  return copy;
};

/** Why the two sides disagree, in terms a user can act on. */
const describeCause = (conflict: ImportRevisionConflict): string => {
  if (conflict.localContentHash === null) {
    return "The content of the version stored here could not be read, so it cannot be compared with the file.";
  }
  if (conflict.contentMatches) {
    return conflict.localDeleted
      ? "The version stored here holds exactly the same content as the file, but it was removed."
      : "The version stored here holds exactly the same content as the file.";
  }
  return conflict.localDeleted
    ? "The removed version stored here holds different content from the one in the file."
    : "The version stored here holds different content from the one in the file.";
};

const ConflictFact = ({ label, value }: { label: string; value: string }) => (
  <div className="flex gap-2 text-sm">
    <span className="font-semibold text-gray-700 shrink-0">{label}</span>
    <span className="text-gray-700 break-all">{value}</span>
  </div>
);

const ResolutionOption = ({
  id,
  name,
  label,
  description,
  checked,
  disabled = false,
  disabledReason,
  onSelect,
}: {
  id: string;
  name: string;
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onSelect: () => void;
}) => (
  <div className={`relative flex items-start ${disabled ? "opacity-60" : ""}`}>
    <div className="absolute flex h-6 items-center">
      <input
        id={id}
        name={name}
        type="radio"
        value={id}
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
        aria-describedby={`${id}-description`}
        className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
      />
    </div>
    <div className="pl-7 text-sm leading-6">
      <label htmlFor={id} className="font-medium text-gray-900">
        {label}
      </label>
      <p id={`${id}-description`} className="text-gray-500">
        {description}
      </p>
      {disabled && disabledReason && (
        <p className="text-red-700" data-testid={`${id}-unavailable`}>
          {disabledReason}
        </p>
      )}
    </div>
  </div>
);

/**
 * The choice for one conflicting revision.
 *
 * Only `availableResolutions` decides what can be picked. `references`, `contentMatches`
 * and `localDeleted` are here to explain the situation, never to re-derive availability:
 * recomputing the rules in the browser would drift the first time the backend changes one.
 *
 * The single exception is the unavailable Replace row, which is rendered disabled with the
 * reason spelled out. Hiding it would leave a user wondering whether the option exists at
 * all, and the reason is the part worth teaching.
 */
const RevisionConflictChoice = ({
  processName,
  conflict,
  selected,
  onSelect,
}: {
  processName: string;
  conflict: ImportRevisionConflict;
  selected?: ImportConflictResolution;
  onSelect: (resolution: ImportConflictResolution) => void;
}) => {
  const groupName = `conflict-resolution-${conflict.revisionUUID}`;
  // Offer nothing rather than guess if the list is missing. An empty list leaves the
  // conflict unanswerable, which keeps the import blocked, which is the safe end of it.
  const offered = conflict.availableResolutions ?? [];
  const replaceOffered = offered.includes(ImportConflictResolution.Replace);

  return (
    <div
      className="space-y-3"
      data-testid={`conflict-${conflict.revisionUUID}`}>
      <p className="text-sm text-gray-800">
        <span className="font-semibold">{processName}</span> in this file is
        version {conflict.revisionUUID} of the process, and this installation
        already has that version. {describeCause(conflict)} A version id means
        the same version everywhere, so the file cannot simply add a second copy
        of it. Tell us what to do.
      </p>

      <div className="space-y-1">
        <ConflictFact label="Version id:" value={conflict.revisionUUID} />
        <ConflictFact
          label="Stored here as:"
          value={`process id ${conflict.localProcessId}${conflict.localDeleted ? " (removed)" : ""}`}
        />
        {conflict.incomingCommitId && (
          <ConflictFact
            label="Arrived from commit:"
            value={conflict.incomingCommitId}
          />
        )}
        {conflict.references.length > 0 && (
          <ConflictFact
            label="Depended on by:"
            value={describeReferences(conflict.references)}
          />
        )}
      </div>

      <fieldset className="space-y-4">
        <legend className="text-sm font-semibold text-gray-900">
          What should happen
        </legend>
        {offered.map(resolution => {
          const copy = resolutionCopy(resolution, conflict.localDeleted);
          return (
            <ResolutionOption
              key={resolution}
              id={`${groupName}-${resolution}`}
              name={groupName}
              label={copy.label}
              description={copy.description}
              checked={selected === resolution}
              onSelect={() => onSelect(resolution)}
            />
          );
        })}
        {!replaceOffered && (
          <ResolutionOption
            id={`${groupName}-${ImportConflictResolution.Replace}`}
            name={groupName}
            label={RESOLUTION_COPY[ImportConflictResolution.Replace].label}
            description={
              RESOLUTION_COPY[ImportConflictResolution.Replace].description
            }
            checked={false}
            disabled
            disabledReason={
              conflict.references.length > 0
                ? `Not available: deleting this version would break ${describeReferences(conflict.references)}. Keep the version already here instead.`
                : "Not available for this version."
            }
            onSelect={() => undefined}
          />
        )}
      </fieldset>

      {selected === ImportConflictResolution.Replace && (
        <p className="text-sm font-semibold text-red-700">
          The version stored here will be deleted when you approve the import.
        </p>
      )}
    </div>
  );
};

const DisplayConflicts = ({
  conflicts,
  resolutions = {},
  onResolutionChange,
}: {
  conflicts: CheckImportViaPipelineEntitiesCheckResult[];
  resolutions?: Record<string, ImportConflictResolution>;
  onResolutionChange?: (
    revisionUUID: string,
    resolution: ImportConflictResolution
  ) => void;
}) => {
  const unresolved = conflicts.filter(
    item =>
      item.revisionConflict && !resolutions[item.revisionConflict.revisionUUID]
  ).length;

  return (
    <>
      {unresolved > 0 && (
        <div
          className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900"
          data-testid="unresolvedConflictsBanner">
          {unresolved === 1
            ? "One process version needs a decision before this import can go ahead."
            : `${unresolved} process versions need a decision before this import can go ahead.`}
        </div>
      )}
      {conflicts.map(category => {
        const conflict = category.revisionConflict;
        const resolved = Boolean(
          conflict && resolutions[conflict.revisionUUID]
        );
        return (
          <Accordion
            title={
              conflict
                ? `${category.name} (${resolved ? "decided" : "needs a decision"})`
                : category.name
            }
            key={category.name}
            initialOpen={Boolean(conflict)}
            bgColor={conflict && resolved ? "bg-green-600" : "bg-red-500"}>
            {conflict ? (
              <RevisionConflictChoice
                processName={category.name}
                conflict={conflict}
                selected={resolutions[conflict.revisionUUID]}
                onSelect={resolution =>
                  onResolutionChange?.(conflict.revisionUUID, resolution)
                }
              />
            ) : (
              <>
                <div className="font-bold text-sm">Conflict Detail</div>
                {/* An overwritable conflict explains itself in warningToUser. The
                    fallback stays for anything that reports a conflict without one. */}
                <div className="max-w-[650px] overflow-auto max-h-[500px] whitespace-pre-line">
                  {category.warningToUser || category.detailedMessage}
                </div>
              </>
            )}
          </Accordion>
        );
      })}
      {conflicts.length === 0 && (
        <div className="flex flex-col justify-center items-center h-40 max-w-[650px]">
          <h1 className="mb-2 text-xl">No Conflicts Found</h1>
          <p className="text-lg text-gray-500">
            Great news! Your pipeline import is ready to proceed. No conflicts
            were detected with existing configurations.
          </p>
        </div>
      )}
    </>
  );
};

export default DisplayConflicts;
